import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import DropzoneUpload from "../Shared/DropzoneUpload";
import Loader from "../Shared/Loader";
import API from "../../utils/api";
import { BASE_URL } from "../../utils/api";
const initialFormData = {
  name: "",
  address: { street: "", city: "", state: "", zipCode: "", country: "" },
  contact: { email: "", phone: "", website: "" },
  businessHours: [
    { day: "Monday", open: "", close: "", isClosed: false },
    { day: "Tuesday", open: "", close: "", isClosed: false },
    { day: "Wednesday", open: "", close: "", isClosed: false },
    { day: "Thursday", open: "", close: "", isClosed: false },
    { day: "Friday", open: "", close: "", isClosed: false },
    { day: "Saturday", open: "", close: "", isClosed: false },
    { day: "Sunday", open: "", close: "", isClosed: false },
  ],
  media: { logo: "", photos: [] },
  description: "",
  categories: [],
  socialMedia: {
    google: "",
    kununu: "",
    trustpilot: "",
    golocal: "",
    jameda: "",
  }

};

const validateField = (name, value) => {
  // Only validate if value is not empty
  if (!value) return "";

  switch (name) {
    case "contact.email":
      return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value)
        ? ""
        : "Invalid email";
    case "contact.phone":
      return /^[0-9\-\+]{9,15}$/.test(value) ? "" : "Invalid phone";
   case "socialMedia.google":
  return /^https?:\/\/(www\.)?google\.com\/maps\/place\/[A-Za-z0-9\-_/]+\/?$/.test(value)
    ? ""
    : "Invalid Google Maps URL. It should start with 'https://www.google.com/maps/place/...'";
    
case "socialMedia.kununu":
  return /^https?:\/\/(www\.)?kununu\.com\/[A-Za-z0-9\-_/]+\/?$/.test(value)
    ? ""
    : "Invalid Kununu profile URL. Example: 'https://www.kununu.com/de/firm-name'";

case "socialMedia.trustpilot":
  return /^https?:\/\/(www\.)?trustpilot\.com\/review\/[A-Za-z0-9\-_.]+\/?$/.test(value)
    ? ""
    : "Invalid Trustpilot URL. It should look like 'https://www.trustpilot.com/review/yourdomain.com'";

case "socialMedia.golocal":
  return /^https?:\/\/(www\.)?golocal\.de\/[A-Za-z0-9\-_/]+\/?$/.test(value)
    ? ""
    : "Invalid GoLocal URL. It should start with 'https://www.golocal.de/...'";
    
case "socialMedia.jameda":
  return /^https?:\/\/(www\.)?jameda\.de\/[A-Za-z0-9\-_/]+\/?$/.test(value)
    ? ""
    : "Invalid Jameda URL. It should look like 'https://www.jameda.de/ort/arztname'";


    case "description":
      return value.length <= 1000 ? "" : "Description too long";
    default:
      return "";
  }
};

const CompanyProfileForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [formMode, setFormMode] = useState("loading");
  const [logoPreview, setLogoPreview] = useState(null);
  const [photos, setPhotos] = useState([]); // holds { preview: string, data: File | string }
  const token = localStorage.getItem("token");
  const payload = JSON.parse(atob(token.split(".")[1])); // decode JWT
  const userRole = payload.role;
  const userId = payload._id || payload.id;
  const [errors, setErrors] = useState({});
  const location = useLocation();
  const { id: companyId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      // Case 1: Create Mode via route
      if (location.pathname.includes("/create-company")) {
        setFormMode("create");
        setFormData(initialFormData);
        setLogoPreview(null);

        return;
      }

      // Case 2: Edit mode with companyId
      if (companyId) {
        try {
          const res = await API.get(`/companies/${companyId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          const data = res.data.data;

          setFormData((prev) => ({
            ...prev,
            ...data,
            address: { ...prev.address, ...(data.address || {}) },
            contact: { ...prev.contact, ...(data.contact || {}) },
            media: { ...prev.media, ...(data.media || {}) },
            socialMedia: { ...prev.socialMedia, ...(data.socialMedia || {}) },
            businessHours: Array.isArray(data.businessHours)
              ? data.businessHours
              : prev.businessHours,
          }));

          if (data.media?.logo) {
            const logoUrl = data.media.logo.startsWith("http")
              ? data.media.logo
              : `${BASE_URL}/${data.media.logo.replace(/^\/+/, "")}`;
            setLogoPreview(logoUrl);
          }

          if (Array.isArray(data.media?.photos)) {
            const photosWithUrls = data.media.photos.map((url) => {
              const fullUrl = url.startsWith("http")
                ? url
                : `${BASE_URL}/${url.replace(/^\/+/, "")}`;
              return { preview: fullUrl, data: url }; // consistent with other usage
            });

            setPhotos(photosWithUrls);
            setFormData((prev) => ({
              ...prev,
              media: { ...prev.media, photos: data.media.photos },
            }));
          }

          setFormMode("edit");
          return;
        } catch (err) {
          console.error("Fetch company by ID error:", err);
        }
      }

      // Case 3: View or create user's own company
      try {
        const res = await API.get("/companies/mine", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (res.data?.data) {
          const data = res.data.data;

          setFormData((prev) => ({
            ...prev,
            ...data,
            address: { ...prev.address, ...(data.address || {}) },
            contact: { ...prev.contact, ...(data.contact || {}) },
            media: { ...prev.media, ...(data.media || {}) },
            socialMedia: { ...prev.socialMedia, ...(data.socialMedia || {}) },
            businessHours: Array.isArray(data.businessHours)
              ? data.businessHours
              : prev.businessHours,
          }));

          if (data.media?.logo) {
            const logoUrl = data.media.logo.startsWith("http")
              ? data.media.logo
              : `${BASE_URL}/${data.media.logo.replace(/^\/+/, "")}`;
            setLogoPreview(logoUrl);
          }

          if (Array.isArray(data.media?.photos)) {
            const loadedPhotos = data.media.photos.map((url) => {
              const fullUrl = url.startsWith("http")
                ? url
                : `${BASE_URL}/${url.replace(/^\/+/, "")}`;
              return { preview: fullUrl, data: url }; // URL as data
            });

            setPhotos(loadedPhotos);
            setFormData((prev) => ({
              ...prev,
              media: {
                ...prev.media,
                photos: data.media.photos,
              },
            }));
          }

          setFormMode("view");
        } else {
          setFormMode("create");
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setFormMode("create");
        } else {
          console.error("Fetch mine error:", err);
        }
      }
    };

    fetchProfile();
  }, [location.pathname, companyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [group, field] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [group]: { ...prev[group], [field]: value },
      }));
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBusinessHourChange = (i, key, value) => {
    const updated = [...formData.businessHours];
    updated[i][key] = value;
    setFormData((prev) => ({ ...prev, businessHours: updated }));
  };

  const handleImageUpload = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === "photos") {
      const newPhotos = files.map((file) => ({
        preview: URL.createObjectURL(file),
        data: file,
      }));

      setPhotos((prev) => [...prev, ...newPhotos]);

      setFormData((prev) => ({
        ...prev,
        media: {
          ...prev.media,
          photos: [...prev.media.photos, ...newPhotos.map((p) => p.data)],
        },
      }));
    }
  };
  const handleDropLogo = (files) => {
    const file = files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        media: { ...prev.media, logo: file },
      }));
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleDropPhotos = (files) => {
    const newPhotos = files.map((file) => ({
      preview: URL.createObjectURL(file),
      data: file,
    }));

    setPhotos((prev) => {
      const updated = [...prev, ...newPhotos];
      setFormData((prevForm) => ({
        ...prevForm,
        media: {
          ...prevForm.media,
          photos: updated.map((p) => p.data),
        },
      }));
      return updated;
    });
  };

  const handleRemovePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      media: {
        ...prev.media,
        photos: prev.media.photos.filter((_, i) => i !== index),
      },
    }));
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setFormData((prev) => ({
      ...prev,
      media: { ...prev.media, logo: "" },
      removeLogo: true, // <== this flag
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const validationFields = [
      "contact.email",
      "contact.phone",
      "contact.website",
      "socialMedia.google",
      "socialMedia.kununu",
      "socialMedia.trustpilot",
      "socialMedia.golocal",
      "socialMedia.jameda",
      "description",
    ];


    const newErrors = {};
    validationFields.forEach((key) => {
      const [group, field] = key.split(".");
      const value = field ? formData[group][field] : formData[key];
      const err = validateField(key, value);
      if (err) newErrors[key] = err;
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const formPayload = new FormData();
    const token = localStorage.getItem("token");
    if (formData.removeLogo) {
      formPayload.append("removeLogo", "true");
    }
    formPayload.append("name", formData.name);
    formPayload.append("description", formData.description);
    formPayload.append("categories", JSON.stringify(formData.categories));
    formPayload.append("address", JSON.stringify(formData.address));
    formPayload.append("contact", JSON.stringify(formData.contact));
    formPayload.append("businessHours", JSON.stringify(formData.businessHours));
    formPayload.append("socialMedia", JSON.stringify(formData.socialMedia));

    if (formData.media.logo instanceof File) {
      formPayload.append("logo", formData.media.logo);
    }
    for (const photo of formData.media.photos) {
      if (photo instanceof File) {
        formPayload.append("photos", photo);
      }
    }

    // Append existing photo URLs once
    const existingPhotos = photos
      .filter((p) => typeof p.data === "string")
      .map((p) => p.data);

    if (existingPhotos.length > 0) {
      formPayload.append("existingPhotos", JSON.stringify(existingPhotos));
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };
      if (formMode === "create") {
        await API.post("/companies", formPayload, config);
      } else if (formMode === "edit") {
        await API.put(`/companies/${formData._id}`, formPayload, config);
      }
      setFormMode("view");
    } catch (err) {
      console.error("Submission error:", err);
    }
    finally {
      setIsSubmitting(false); // 👈 Hide loader
    }
  };

  const isDisabled = formMode === "view";

  // if (formMode === "loading")
  //   return <div className="text-center py-10">Loading...</div>;

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full  p-6 pt-0 bg-white shadow-lg rounded-lg relative"
    >
      {/* Sticky Navbar */}
      <div className="sticky w-full top-0 z-10 bg-white border-b border-gray-200 py-3 mb-6 flex justify-between items-center px-2 shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900">
          {formMode === "create"
            ? "Create Company Profile"
            : "Update Company Profile"}
        </h2>

        <div className="space-x-2">
          {formMode === "view" && (
            <button
              onClick={() => setFormMode("edit")}
              type="button"
              className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition"
            >
              Edit
            </button>
          )}

          {formMode !== "view" && (
            <button
              type="submit"
              className="bg-gray-900 text-white px-4 py-2 rounded rounded-md hover:bg-gray-700 transition"
            >
              {formMode === "create" ? "Create Profile" : "Update Profile"}
            </button>
          )}
        </div>
      </div>
      {formMode === "loading" ? (
        <Loader message="Fetching data..." />
      ) : (
        <>
          {/* Name and Address */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label>Company Name</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isDisabled}
                required
                className="w-full border border-gray-200 rounded outline-none focus:ring-0 rounded px-3 py-2"
              />
            </div>

            {["street", "city", "state", "zipCode", "country"].map((field) => (
              <div key={field}>
                <label className="capitalize">{field}</label>
                <input
                  name={`address.${field}`}
                  value={formData.address[field]}
                  onChange={handleChange}
                  disabled={isDisabled}
                  required
                  className="w-full border border-gray-200 rounded outline-none focus:ring-0 rounded px-3 py-2"
                />
              </div>
            ))}

            {["email", "phone", "website"].map((field) => (
              <div key={field}>
                <label className="capitalize">{field}</label>
                <input
                  name={`contact.${field}`}
                  value={formData.contact[field]}
                  onChange={handleChange}
                  disabled={isDisabled}
                  required={field !== "website"}
                  className="w-full border border-gray-200 rounded outline-none focus:ring-0 rounded px-3 py-2"
                />
                {errors[`contact.${field}`] && (
                  <p className="text-red-500 text-sm">
                    {errors[`contact.${field}`]}
                  </p>
                )}
              </div>
            ))}
          </div>
          {/* Description */}
          <div className="mt-4">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={isDisabled}
              className="w-full border border-gray-200 rounded outline-none focus:ring-0 rounded px-3 py-2"
            />
            {errors.description && (
              <p className="text-red-500 text-sm">{errors.description}</p>
            )}
          </div>
          {/* Business Hours */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2">Business Hours</h3>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border-gray-200 px-4 py-2 text-left">Day</th>
                    <th className="border-gray-200 px-4 py-2 text-left">
                      Open
                    </th>
                    <th className="border-gray-200 px-4 py-2 text-left">
                      Close
                    </th>
                    <th className="border-gray-200 px-4 py-2 text-left">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {formData.businessHours.map((hour, i) => (
                    <tr key={hour.day} className="even:bg-gray-50">
                      <td className="border-gray-200 px-4 py-2">{hour.day}</td>
                      <td className="border-gray-200 px-4 py-2">
                        <input
                          type="text"
                          value={hour.open}
                          onChange={(e) =>
                            handleBusinessHourChange(i, "open", e.target.value)
                          }
                          disabled={isDisabled}
                          className="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none"
                        />
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="text"
                          value={hour.close}
                          onChange={(e) =>
                            handleBusinessHourChange(i, "close", e.target.value)
                          }
                          disabled={isDisabled}
                          className="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none"
                        />
                      </td>
                      <td className="border px-4 py-2">
                        <label className="inline-flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={hour.isClosed}
                            onChange={(e) =>
                              handleBusinessHourChange(
                                i,
                                "isClosed",
                                e.target.checked
                              )
                            }
                            disabled={isDisabled}
                          />
                          Closed
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Logo + Photos */}
          <div className="flex flex-col md:flex-row gap-6 mt-4">
            <div className="md:w-1/2">
              <DropzoneUpload
                label="Upload Logo(Maximum 1)"
                onDrop={handleDropLogo}
                previews={logoPreview ? [logoPreview] : []}
                onRemove={handleRemoveLogo}
                multiple={false}
                disabled={isDisabled}
              />
            </div>

            {/* Vertical divider */}
            <div className="hidden md:flex items-center">
              <div className="h-full w-px bg-gray-300" />
            </div>

            <div className="md:w-1/2">
              <DropzoneUpload
                label="Upload Photos(Maximum 5)"
                onDrop={handleDropPhotos}
                previews={photos.map((p) => p.preview)}
                onRemove={handleRemovePhoto}
                multiple={true}
                disabled={isDisabled}
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mt-4">
            <label>Categories (comma separated)</label>
            <input
              value={formData.categories.join(",")}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  categories: e.target.value.split(","),
                }))
              }
              disabled={isDisabled}
              className="w-full border border-gray-200 rounded outline-none focus:ring-0 rounded px-3 py-2"
            />
          </div>

          {/* Social Media */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {["google", "kununu", "trustpilot", "golocal", "jameda"].map((field) => (

              <div key={field}>
                <label className="capitalize">{field}</label>
                <input
                  name={`socialMedia.${field}`}
                  value={formData.socialMedia[field]}
                  onChange={handleChange}
                  disabled={isDisabled}
                  className="w-full border border-gray-200 rounded outline-none focus:ring-0 rounded px-3 py-2"
                />
                {errors[`socialMedia.${field}`] && (
                  <p className="text-red-500 text-sm">
                    {errors[`socialMedia.${field}`]}
                  </p>
                )}
              </div>
            ))}
          </div>
          {isSubmitting && (
            <div className="fixed inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
              <Loader message="Saving changes..." />
            </div>
          )}

        </>
      )}
    </form>

  );

};

export default CompanyProfileForm;
